import { Badge } from "@fluentui/react-components";
import type { StatoDocumento, Valutazione } from "@app/schema";
import { giorniAlRinnovo, giorniAllaScadenza, statoIscrizione, statoVisuale, type Gara } from "../data/gare";

const COLORI: Record<Valutazione, "informative" | "success" | "subtle"> = {
  "Da valutare": "informative",
  Partecipa: "success",
  "Non partecipa": "subtle",
};

export function ValutazioneBadge({ g }: { g: Gara }) {
  const v = (g.fields.Valutazione ?? "Da valutare") as Valutazione;
  return (
    <Badge appearance="tint" color={COLORI[v]} shape="rounded">
      {v}
    </Badge>
  );
}

/** Scadenza: rossa entro 5 giorni, grigia se scaduta o archiviata. */
export function ScadenzaBadge({ g }: { g: Gara }) {
  const stato = statoVisuale(g);
  if (stato === "Archiviata")
    return (
      <Badge appearance="tint" color="subtle" shape="rounded">
        Archiviata
      </Badge>
    );
  const giorni = giorniAllaScadenza(g);
  if (giorni === undefined) return null;
  if (stato === "Scaduta")
    return (
      <Badge appearance="tint" color="subtle" shape="rounded">
        Scaduta
      </Badge>
    );
  const testo = giorni === 0 ? "Scade oggi" : giorni === 1 ? "Scade domani" : `Scade tra ${giorni} giorni`;
  return (
    <Badge appearance={giorni <= 5 ? "filled" : "tint"} color={giorni <= 5 ? "danger" : "informative"} shape="rounded">
      {testo}
    </Badge>
  );
}

/** Esito della gara (o riepilogo dei lotti, es. "2 lotti su 3 aggiudicati"). */
export function EsitoBadge({ esito }: { esito?: string }) {
  if (!esito) return null;
  // "Aggiudicata" oppure "2 lotti aggiudicati su 3"
  const vinta = esito === "Aggiudicata" || /^\d/.test(esito);
  return (
    <Badge appearance="tint" color={vinta ? "success" : "warning"} shape="rounded">
      {esito}
    </Badge>
  );
}

const COLORI_DOCUMENTO: Record<StatoDocumento, "informative" | "warning" | "brand" | "success"> = {
  "da fare": "informative",
  provvisorio: "brand",
  "da rivedere": "warning",
  definitivo: "success",
  approvato: "success",
};
export const coloreStatoDocumento = (s?: StatoDocumento) => COLORI_DOCUMENTO[s ?? "da fare"];

/** Stato di un documento della checklist. */
export function StatoDocumentoBadge({ stato }: { stato?: StatoDocumento }) {
  return (
    <Badge appearance={stato === "approvato" ? "filled" : stato === "da fare" || !stato ? "outline" : "tint"} color={coloreStatoDocumento(stato)} shape="rounded">
      {stato ?? "da fare"}
    </Badge>
  );
}

/** Stato dell'iscrizione a un albo: da iscrivere, iscritta, in scadenza (30 giorni), scaduta. */
export function IscrizioneBadge({ g }: { g: Gara }) {
  if (g.fields.Stato === "Archiviata")
    return (
      <Badge appearance="tint" color="subtle" shape="rounded">
        Archiviato
      </Badge>
    );
  const stato = statoIscrizione(g);
  const giorni = giorniAlRinnovo(g);
  if (stato === "Da iscrivere")
    return (
      <Badge appearance="outline" color="informative" shape="rounded">
        Da iscrivere
      </Badge>
    );
  if (stato === "Scaduta")
    return (
      <Badge appearance="filled" color="danger" shape="rounded">
        Iscrizione scaduta
      </Badge>
    );
  if (stato === "In scadenza")
    return (
      <Badge appearance="filled" color="warning" shape="rounded">
        {giorni === 0 ? "Rinnovo oggi" : giorni === 1 ? "Rinnovo domani" : `Rinnovo tra ${giorni} giorni`}
      </Badge>
    );
  return (
    <Badge appearance="tint" color="success" shape="rounded">
      Iscritta
    </Badge>
  );
}
